/**
 * Design Pipeline Store
 *
 * Tracks one in-flight (or completed) design-pipeline session: prompt →
 * features → DESIGN.md → manifest → styles.css → components.
 *
 * Also tracks the chat thread + version chain for the design "root" so the
 * preview screen can drive refinement and version switching.
 */

import type { AppKind, AssetMeta } from "@mash/types";
import { create } from "zustand";

import type {
  DesignMessage,
  DesignMessageKind,
  PublishedAppSummary,
  VersionSummary,
} from "../lib/designApi";

/**
 * Assistant chat-message kinds whose `content` is a free-text reply that the
 * backend now streams to the SSE bus as `assistant_message_delta` events.
 * When a message of one of these kinds lands (either via `chat_message` from
 * the SSE bus or a locally-appended message from a refine HTTP response),
 * the streaming typewriter buffer is the now-stale duplicate and gets
 * cleared.
 *
 * `version_ready` is excluded — it's rendered as a fixed version card, not a
 * text bubble, and never goes through the streaming buffer.
 */
const ASSISTANT_TEXT_KINDS = new Set<DesignMessageKind>([
  "features_proposed",
  "clarify",
  "decline",
  "discuss",
  "runner_error_ack",
]);

export interface PipelineFeature {
  description: string;
  enabled: boolean;
}

export interface PipelineAppConcept {
  name: string;
  purpose: string;
  primaryAction: string;
}

export interface PipelineManifestFile {
  path: string;
  role: "icons" | "components" | "screen" | "app" | "other";
  dependencies: string[];
  description: string;
}

export interface PipelineManifest {
  files: PipelineManifestFile[];
}

export type PipelineStatus =
  | "idle"
  | "starting"
  | "features"
  | "confirmed"
  | "building"
  | "done"
  | "failed"
  /**
   * The backend parked this session in `status='paused'`. Either the user
   * clicked Pause (`pausedReason='user'`) or the quota gate pre-empted the
   * build (`pausedReason='quota_exceeded'`). The composer button surfaces a
   * Play icon; clicking it calls `resumeDesign` to re-enter the build loop.
   */
  | "paused";

export interface PipelineReviewIssue {
  description: string;
  severity: "low" | "medium" | "high";
  screen?: string;
}

export type ReviewState = "idle" | "running" | "done";

export type SeedData = Record<string, Record<string, unknown>[]>;

/**
 * A temporary, view-only snapshot of a *past* version, loaded when the user
 * taps a version card in the chat. It overrides the live session artifacts in
 * the preview pane without touching the persistent "current version" pointer
 * — so refining and saving still target the real current version. Cleared the
 * moment a build starts (`beginRefine` / `addPendingVersion`), when a build
 * finishes (`markPipelineDone`), on `reset`, or when the user taps back to the
 * live current version. `null` whenever no temporary preview is active.
 */
export interface PreviewVersion {
  versionId: string;
  /** True while the version's artifacts are still being fetched. */
  loading: boolean;
  bundleUrl: string | null;
  stylesCss: string;
  seedData: SeedData | null;
  contentCollections: string[];
  kind: AppKind | null;
  usesMotion: boolean;
  assets: AssetMeta[];
}

export interface DesignPipelineSession {
  prompt: string;
  status: PipelineStatus;
  /**
   * Decided by the backend's classify step (or the mock's keyword router).
   * `null` until the `kind_decided` SSE event arrives. Consumed by
   * DesignAppRunnerEmbed to pick the right runner component; treat `null`
   * the same as `"app"` since classify always runs before any runnable
   * artifact exists.
   */
  kind: AppKind | null;
  /** Human-readable one-liner from the classifier; surfaced in the kind chip. */
  kindRationale: string | null;
  /**
   * Whether the classifier decided this app uses motion sensors. Drives
   * whether the runner surfaces the iOS motion-permission gate. Defaults
   * to `false`; set on the `kind_decided` SSE event and on hydrate.
   */
  usesMotion: boolean;
  appConcept: PipelineAppConcept | null;
  features: PipelineFeature[];
  designMd: string;
  manifest: PipelineManifest | null;
  stylesCss: string;
  /**
   * Set when the backend emits `typecheck_done` with `final: true` — i.e.
   * the typecheck/repair loop has finished. Used by BuildProgressBubble
   * to advance past the components milestone before styles.css arrives
   * (otherwise the bar stalls for ~2 min during the silent repair phase).
   */
  typecheckDone: boolean;
  components: Record<string, string>;
  completedFiles: string[];
  currentFile: string | null;
  reviewState: ReviewState;
  reviewSummary: string;
  reviewIssues: PipelineReviewIssue[];
  bundleUrl: string | null;
  seedData: SeedData | null;
  /**
   * Names of the collections in {@link seedData} whose records are the app's
   * content (a curated reference dataset) rather than illustrative samples —
   * those get materialized into every user's data on publish. Set on the
   * `seed_data_done` SSE event. No UI consumer yet; kept for parity with the
   * server and for a future "ships to everyone" hint.
   */
  contentCollections: string[];
  /**
   * Assets the asset-plan step downloaded for this in-progress build.
   * Empty array until the `assets_ready` SSE event fires (game2d /
   * experience3d only — standard apps don't run the asset plan).
   */
  assets: AssetMeta[];
  error: string | null;
  /**
   * Cause of the most recent pause, or null while not paused. Tracks the
   * server's `paused_reason` column verbatim. Used to distinguish a user-
   * triggered pause (button shows Play) from a quota pause (button shows
   * Play, plus a quota-message banner) — both share `status='paused'`.
   */
  pausedReason: "user" | "quota_exceeded" | null;
  /**
   * True from the moment the user clicks Pause / Play until the
   * server-side `session_paused` / `session_resumed` event lands. Renders
   * the composer button in a spinner state so a fast double-tap doesn't
   * fire a second request.
   */
  pauseRequestPending: boolean;

  // Refinement / versioning
  rootId: string | null;
  currentVersionId: string | null;
  /**
   * Temporary view-only preview of a past version (set by tapping a version
   * card), or `null` when the preview pane reflects the live current version.
   * See {@link PreviewVersion}.
   */
  previewVersion: PreviewVersion | null;
  versions: VersionSummary[];
  messages: DesignMessage[];
  /**
   * True from the moment the user submits a refinement request until the
   * resulting build finishes (or the backend declines / asks to clarify, or
   * the request errors out). This is the authoritative "a refine is running
   * right now" signal — UI that needs to distinguish a fresh build from a
   * settled one should key off this rather than inferring from `status`.
   */
  refining: boolean;
  /**
   * Snapshot of the previous build's status and artifacts, captured by
   * `beginRefine` the instant a refinement request goes in flight.
   * `beginRefine` clears the live artifacts immediately and marks the session
   * building (so the progress UI restarts from 0% during the round-trip to the
   * backend instead of inheriting the finished build's "Done" state); if the
   * backend then declines / asks to clarify / errors, `cancelRefine` restores
   * this snapshot. `null` whenever no refine is in flight.
   */
  refineSnapshot: RefineSnapshot | null;

  // Previously-published name/icon for this design root, if the user already
  // saved this app at least once. Used to prefill the Save screen.
  publishedApp: PublishedAppSummary | null;

  /**
   * Wall-clock ms at which the current build kicked off — i.e. the moment the
   * user approved the feature checklist, accepted a refinement, or retried a
   * failed build. Read at `pipeline_done` to derive the
   * `generationDurationMs` property of the `mash_generated_success` analytics
   * event. Deliberately anchored at build start rather than prompt submit, so
   * the metric measures machine time and not how long the user spent reading
   * the feature checklist.
   *
   * `null` when no build has started in this mount — notably after a refresh
   * mid-build, where we never observed the start and so report no duration.
   */
  generationStartedAt: number | null;

  /**
   * Live typewriter buffer for whatever free-text assistant chat reply is
   * currently streaming — the `features_proposed` text during planning,
   * the `clarify` / `decline` / accept-`discuss` reply from refine, or
   * the standalone `discuss` reply. The backend pushes deltas via
   * `assistant_message_delta` SSE events; the chat panel renders this
   * buffer as a single neutral assistant bubble until the persisted
   * `chat_message` (or a locally-appended message from the refine HTTP
   * response) lands, at which point the buffer is cleared.
   *
   * `null` when no streaming reply is in flight (idle, or already replaced
   * by the persisted message).
   */
  streamingAssistantMessage: string | null;
}

type RefineSnapshot = Pick<
  DesignPipelineSession,
  | "designMd"
  | "manifest"
  | "stylesCss"
  | "typecheckDone"
  | "components"
  | "completedFiles"
  | "currentFile"
  | "reviewState"
  | "reviewSummary"
  | "reviewIssues"
  | "bundleUrl"
  | "seedData"
  | "contentCollections"
  | "assets"
  | "error"
>;

interface DesignPipelineActions {
  reset: () => void;
  setPrompt: (prompt: string) => void;
  setStatus: (status: PipelineStatus) => void;
  setError: (error: string | null) => void;
  setKind: (kind: AppKind, rationale: string, usesMotion: boolean) => void;
  setAssets: (assets: AssetMeta[]) => void;
  setSessionStart: (args: {
    appConcept: PipelineAppConcept;
    features: PipelineFeature[];
  }) => void;
  hydrateFromServer: (raw: unknown) => void;
  appendDesignMdDelta: (delta: string) => void;
  setDesignMd: (designMd: string) => void;
  setManifest: (manifest: PipelineManifest) => void;
  setStylesCss: (stylesCss: string) => void;
  markTypecheckDone: () => void;
  startComponent: (path: string) => void;
  completeComponent: (path: string, content: string) => void;
  startReview: () => void;
  completeReview: (summary: string, issues: PipelineReviewIssue[]) => void;
  setBundleUrl: (bundleUrl: string) => void;
  setSeedData: (seedData: SeedData, contentCollections: string[]) => void;
  /**
   * Stamp {@link DesignPipelineSession.generationStartedAt}. Call at every
   * point a build actually begins: feature-checklist confirm, accepted
   * refinement, and retry of a failed build.
   */
  markBuildStarted: () => void;
  markPipelineDone: () => void;
  /** Server confirmed the session is parked at status='paused'. */
  setPaused: (reason: "user" | "quota_exceeded") => void;
  /**
   * Server confirmed the session has been re-entered. `phase` mirrors the
   * `session_resumed` event: `"planning"` lands on `starting` (planning runs
   * again and a fresh `feature_list` is still to come), anything else lands
   * on `building`.
   */
  setResumed: (phase?: "planning" | "build") => void;
  /** Toggle the spinner state while a pause/resume request is in flight. */
  setPauseRequestPending: (value: boolean) => void;
  // Feature edits
  toggleFeature: (index: number) => void;
  appendFeature: (feature: PipelineFeature) => void;
  setFeatures: (features: PipelineFeature[]) => void;
  // Conversation / versions
  setConversation: (args: {
    messages: DesignMessage[];
    versions: VersionSummary[];
    currentVersionId: string | null;
    publishedApp?: PublishedAppSummary | null;
  }) => void;
  appendLocalMessage: (msg: {
    role: DesignMessage["role"];
    kind: DesignMessageKind;
    content: string;
    metadata?: Record<string, unknown> | null;
    versionId?: string | null;
  }) => void;
  appendChatMessage: (message: DesignMessage) => void;
  setRefining: (value: boolean) => void;
  /**
   * Reset the per-build artifacts (and clear `error`) so the
   * BuildProgressBubble restarts from 0%. Used when retrying a failed build or
   * refine — the failed attempt may have left partial progress in the store.
   */
  resetBuildArtifacts: () => void;
  /**
   * Mark a refinement request as in flight: flips `refining` on, stashes the
   * current build's artifacts into `refineSnapshot`, and clears the live
   * artifacts so the build-progress UI restarts from scratch. Pair with
   * `cancelRefine` (request declined / clarify / errored) or
   * `addPendingVersion` (request accepted — the snapshot is dropped).
   */
  beginRefine: () => void;
  /** Undo `beginRefine`: restore the snapshot and clear `refining`. */
  cancelRefine: () => void;
  setCurrentVersionId: (id: string | null) => void;
  /**
   * Start a temporary, view-only preview of a past version. Sets
   * {@link PreviewVersion} in a loading state; pair with
   * `setPreviewVersionData` once the version's artifacts have been fetched, or
   * `clearPreviewVersion` to abandon it.
   */
  beginPreviewVersion: (versionId: string) => void;
  /**
   * Fill in a previewed version's artifacts. No-op unless the active preview
   * still targets `versionId` — guards against a stale fetch resolving after
   * the user has tapped a different card (or cleared the preview).
   */
  setPreviewVersionData: (
    versionId: string,
    data: Omit<PreviewVersion, "versionId" | "loading">,
  ) => void;
  /** Drop the temporary preview; the pane returns to the live current version. */
  clearPreviewVersion: () => void;
  addPendingVersion: (versionId: string, version: number) => void;
  /**
   * Append a chunk of the currently-streaming assistant chat reply. Mirrors
   * the server's `assistant_message_delta` SSE event. Idempotent after a
   * `null` reset (further deltas restart the buffer), so a fresh refine /
   * discuss / planning round on the same session surfaces cleanly.
   */
  appendStreamingAssistantMessage: (delta: string) => void;
  /**
   * Replace the streaming buffer with the final value (or null to clear).
   * Called on `assistant_message_done` to reconcile late subscribers and on
   * the persisted `chat_message` arrival to hand off to the authoritative
   * bubble.
   */
  setStreamingAssistantMessage: (value: string | null) => void;
}

const INITIAL: DesignPipelineSession = {
  prompt: "",
  status: "idle",
  kind: null,
  kindRationale: null,
  usesMotion: false,
  appConcept: null,
  features: [],
  designMd: "",
  manifest: null,
  stylesCss: "",
  typecheckDone: false,
  components: {},
  completedFiles: [],
  currentFile: null,
  reviewState: "idle",
  reviewSummary: "",
  reviewIssues: [],
  bundleUrl: null,
  seedData: null,
  contentCollections: [],
  assets: [],
  error: null,
  pausedReason: null,
  pauseRequestPending: false,
  rootId: null,
  currentVersionId: null,
  previewVersion: null,
  versions: [],
  messages: [],
  refining: false,
  refineSnapshot: null,
  publishedApp: null,
  generationStartedAt: null,
  streamingAssistantMessage: null,
};

/**
 * Per-build artifacts reset to their pristine state whenever a *new* build
 * starts — a refine, an accepted refine, or a retry of a failed build. Keeping
 * the field list in one place means the BuildProgressBubble restarts from 0%
 * across every entry point instead of inheriting a prior build's progress or
 * its finished "Done" state. Callers spread this and layer on their own
 * `error` handling (refine clears it; retry clears it; accept leaves it).
 */
const BUILD_ARTIFACTS_RESET = {
  designMd: "",
  manifest: null,
  stylesCss: "",
  typecheckDone: false,
  components: {},
  completedFiles: [],
  currentFile: null,
  reviewState: "idle",
  reviewSummary: "",
  reviewIssues: [],
  bundleUrl: null,
  seedData: null,
  contentCollections: [],
  assets: [],
} satisfies Partial<DesignPipelineSession>;

export const useDesignPipelineStore = create<
  DesignPipelineSession & DesignPipelineActions
>(set => ({
  ...INITIAL,

  reset: () => set({ ...INITIAL }),
  setPrompt: prompt => set({ prompt }),
  setStatus: status => set({ status }),
  setError: error => set({ error }),
  setKind: (kind, rationale, usesMotion) =>
    set({ kind, kindRationale: rationale, usesMotion }),
  setAssets: assets => set({ assets }),

  setSessionStart: ({ appConcept, features }) =>
    set(state => {
      // `feature_list` is authoritative only while planning is in progress.
      // The SSE bus replays buffered planning events whenever a client
      // reconnects, so accepting one after the checklist becomes editable
      // would overwrite user additions/removals with the original plan.
      // Ignore the whole stale event atomically: concept, features, status,
      // and error must all remain untouched.
      if (state.status !== "idle" && state.status !== "starting") {
        return state;
      }
      return {
        appConcept,
        features: features.map(f => ({
          description: f.description,
          enabled: f.enabled ?? true,
        })),
        status: "features",
        error: null,
      };
    }),

  hydrateFromServer: raw => {
    const r = (raw || {}) as Partial<{
      id: string;
      rootId: string;
      version: number;
      isCurrent: boolean;
      prompt: string;
      status: string;
      appConcept: PipelineAppConcept;
      features: PipelineFeature[];
      designMd: string;
      manifest: PipelineManifest | null;
      stylesCss: string;
      components: Record<string, string>;
      bundleUrl: string | null;
      seedData: SeedData | null;
      contentCollections: string[];
      /**
       * Read on hydrate so a page refresh on a finished game/3D session
       * dispatches to the correct runner. Without this, `kind` stays at
       * the initial `null` value and the embed falls through to
       * DesignAppRunner — which doesn't install Phaser/THREE/R3F globals,
       * so the bundle crashes with "ReferenceError: Phaser is not defined".
       */
      kind: AppKind | null;
      usesMotion: boolean;
      assets: AssetMeta[];
      error: string | null;
      pausedReason: "user" | "quota_exceeded" | null;
    }>;

    const status = mapServerStatus(r.status);
    const components = r.components || {};
    set({
      prompt: r.prompt ?? "",
      status,
      appConcept: r.appConcept ?? null,
      features: (
        (r.features ?? []) as Array<
          Partial<PipelineFeature> & { name?: string }
        >
      ).map(f => ({
        description: (f.description ?? "").trim() || (f.name ?? "").trim(),
        enabled: f.enabled === undefined ? true : Boolean(f.enabled),
      })),
      designMd: r.designMd ?? "",
      manifest: r.manifest ?? null,
      stylesCss: r.stylesCss ?? "",
      // We don't persist this on the server; rehydrate as done iff CSS
      // exists (typecheck always finishes before styles.css in the
      // pipeline order, so this is a safe inference on refresh).
      typecheckDone: Boolean(r.stylesCss),
      components,
      completedFiles: Object.keys(components),
      currentFile: null,
      bundleUrl: r.bundleUrl ?? null,
      seedData: r.seedData ?? null,
      contentCollections: r.contentCollections ?? [],
      // `kind` defaults to null (treated as "app" by the runner dispatch)
      // for back-compat with sessions that predate the kind column.
      kind: r.kind ?? null,
      usesMotion: Boolean(r.usesMotion),
      assets: r.assets ?? [],
      error: r.error ?? null,
      pausedReason: status === "paused" ? (r.pausedReason ?? null) : null,
      pauseRequestPending: false,
      rootId: r.rootId ?? r.id ?? null,
      currentVersionId: r.id ?? null,
    });
  },

  appendDesignMdDelta: delta =>
    set(state => ({ designMd: state.designMd + delta })),
  setDesignMd: designMd => set({ designMd }),
  setManifest: manifest => set({ manifest }),
  setStylesCss: stylesCss => set({ stylesCss }),
  markTypecheckDone: () => set({ typecheckDone: true }),

  startComponent: path => set({ currentFile: path }),

  completeComponent: (path, content) =>
    set(state => ({
      components: { ...state.components, [path]: content },
      completedFiles: state.completedFiles.includes(path)
        ? state.completedFiles
        : [...state.completedFiles, path],
      currentFile: null,
    })),

  startReview: () => set({ reviewState: "running" }),
  completeReview: (summary, issues) =>
    set({ reviewState: "done", reviewSummary: summary, reviewIssues: issues }),
  setBundleUrl: bundleUrl => set({ bundleUrl }),
  setSeedData: (seedData, contentCollections) =>
    set({ seedData, contentCollections }),

  markBuildStarted: () => set({ generationStartedAt: Date.now() }),

  markPipelineDone: () =>
    set(state => ({
      status: "done",
      currentFile: null,
      refining: false,
      refineSnapshot: null,
      // A finished build supersedes any version the user was peeking at.
      previewVersion: null,
      versions: state.versions.map(v => ({
        ...v,
        status: v.id === state.currentVersionId ? "done" : v.status,
      })),
    })),

  setPaused: reason =>
    set({
      status: "paused",
      pausedReason: reason,
      pauseRequestPending: false,
      currentFile: null,
    }),

  setResumed: phase =>
    set({
      // "starting" keeps the session inside the window where `setSessionStart`
      // accepts a feature plan — a planning resume republishes `feature_list`,
      // and landing on "building" would make the guard swallow it and strand
      // the user on an empty progress bar.
      status: phase === "planning" ? "starting" : "building",
      pausedReason: null,
      pauseRequestPending: false,
      error: null,
    }),

  setPauseRequestPending: value => set({ pauseRequestPending: value }),

  toggleFeature: index =>
    set(state => ({
      features: state.features.map((f, i) =>
        i === index ? { ...f, enabled: !f.enabled } : f,
      ),
    })),

  appendFeature: feature =>
    set(state => ({ features: [...state.features, feature] })),

  setFeatures: features => set({ features }),

  setConversation: ({ messages, versions, currentVersionId, publishedApp }) =>
    set(
      publishedApp === undefined
        ? { messages, versions, currentVersionId }
        : { messages, versions, currentVersionId, publishedApp },
    ),

  appendLocalMessage: msg =>
    set(state => {
      // Locally-appended assistant chat replies (currently the
      // accept-branch `discuss` reply from refineDesign) finalise the
      // streaming buffer the same way persisted ones do.
      const clearStreaming =
        msg.role === "assistant" &&
        ASSISTANT_TEXT_KINDS.has(msg.kind) &&
        state.streamingAssistantMessage !== null;
      const next = {
        messages: [
          ...state.messages,
          {
            id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            rootId: state.rootId ?? "",
            role: msg.role,
            kind: msg.kind,
            content: msg.content,
            versionId: msg.versionId ?? null,
            metadata: msg.metadata ?? null,
            createdAt: new Date().toISOString(),
          },
        ],
      };
      return clearStreaming
        ? { ...next, streamingAssistantMessage: null }
        : next;
    }),

  appendChatMessage: message =>
    set(state => {
      const dedup = state.messages.some(m => m.id === message.id);
      // The persisted assistant chat reply is the authoritative version of
      // anything currently in the typewriter buffer — drop the buffer so
      // we don't double-render.
      const clearStreaming =
        message.role === "assistant" &&
        ASSISTANT_TEXT_KINDS.has(message.kind) &&
        state.streamingAssistantMessage !== null;
      if (dedup) {
        return clearStreaming ? { streamingAssistantMessage: null } : {};
      }
      return clearStreaming
        ? {
            messages: [...state.messages, message],
            streamingAssistantMessage: null,
          }
        : { messages: [...state.messages, message] };
    }),

  setRefining: value => set({ refining: value }),

  resetBuildArtifacts: () => set({ ...BUILD_ARTIFACTS_RESET, error: null }),

  beginRefine: () =>
    set(state => {
      // Re-entrancy guard: the composer is disabled while `refining`, but
      // bail rather than clobber an existing snapshot if we somehow get here
      // twice.
      if (state.refining) return {};
      return {
        refining: true,
        // A new build supersedes any version the user was peeking at.
        previewVersion: null,
        refineSnapshot: {
          designMd: state.designMd,
          manifest: state.manifest,
          stylesCss: state.stylesCss,
          typecheckDone: state.typecheckDone,
          components: state.components,
          completedFiles: state.completedFiles,
          currentFile: state.currentFile,
          reviewState: state.reviewState,
          reviewSummary: state.reviewSummary,
          reviewIssues: state.reviewIssues,
          bundleUrl: state.bundleUrl,
          seedData: state.seedData,
          contentCollections: state.contentCollections,
          assets: state.assets,
          error: state.error,
        },
        // Clear the live artifacts now so the BuildProgressBubble (and the
        // runner spinner) reflect the *new* build from 0% during the
        // round-trip to the backend instead of inheriting the finished
        // build's "Done" state. `cancelRefine` puts these back if the
        // request doesn't turn into a build; `addPendingVersion` re-clears
        // them (and drops the snapshot) if it does.
        ...BUILD_ARTIFACTS_RESET,
        error: null,
      };
    }),

  cancelRefine: () =>
    set(state => ({
      ...state.refineSnapshot,
      refining: false,
      refineSnapshot: null,
    })),

  setCurrentVersionId: id => set({ currentVersionId: id }),

  beginPreviewVersion: versionId =>
    set({
      previewVersion: {
        versionId,
        loading: true,
        bundleUrl: null,
        stylesCss: "",
        seedData: null,
        contentCollections: [],
        kind: null,
        usesMotion: false,
        assets: [],
      },
    }),

  setPreviewVersionData: (versionId, data) =>
    set(state =>
      state.previewVersion?.versionId === versionId
        ? { previewVersion: { versionId, loading: false, ...data } }
        : {},
    ),

  clearPreviewVersion: () => set({ previewVersion: null }),

  appendStreamingAssistantMessage: delta =>
    set(state => ({
      streamingAssistantMessage:
        (state.streamingAssistantMessage ?? "") + delta,
    })),

  setStreamingAssistantMessage: value =>
    set({ streamingAssistantMessage: value }),

  addPendingVersion: (versionId, version) =>
    set(state => {
      if (state.versions.some(v => v.id === versionId))
        return { refineSnapshot: null };
      return {
        // The refinement was accepted, so the snapshot of the prior build is
        // no longer needed — drop it.
        refineSnapshot: null,
        // A new build supersedes any version the user was peeking at.
        previewVersion: null,
        // Reset per-build artifacts so the BuildProgressBubble starts at
        // 0% instead of inheriting the previous build's "Done" state.
        // (`beginRefine` already did this for the refine path, but keep it
        // here too so this is correct for any direct caller.)
        ...BUILD_ARTIFACTS_RESET,
        versions: [
          ...state.versions.map(v => ({ ...v, isCurrent: false })),
          {
            id: versionId,
            rootId: state.rootId ?? "",
            version,
            status: "building",
            isCurrent: true,
            createdAt: new Date().toISOString(),
            refinementRequest: null,
          },
        ],
        currentVersionId: versionId,
      };
    }),
}));

function mapServerStatus(s: string | undefined): PipelineStatus {
  switch (s) {
    case "pending":
      return "starting";
    case "features_ready":
      return "features";
    case "building":
      return "building";
    case "done":
      return "done";
    case "failed":
      return "failed";
    case "paused":
      return "paused";
    default:
      return "idle";
  }
}
